import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileUpdated?: (profile: UserProfile) => void;
}

interface UserProfile {
  userId: number;
  email: string;
  name: string;
  phone: string;
  companyName: string;
  address: string;
  postalCode: string;
  city: string;
  cvr: string;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, onProfileUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form fields
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [cvr, setCvr] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadProfile();
    }
  }, [isOpen]);

  const loadProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const profile = await apiService.getUserProfile();
      setEmail(profile.email || '');
      setName(profile.name || '');
      setPhone(profile.phone || '');
      setCompanyName(profile.companyName || '');
      setAddress(profile.address || '');
      setPostalCode(profile.postalCode || '');
      setCity(profile.city || '');
      setCvr(profile.cvr || '');
    } catch (err: any) {
      setError(err.message || 'Kunne ikke hente profil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate password if provided
    if (password && password !== confirmPassword) {
      setError('Adgangskoder matcher ikke');
      return;
    }

    if (password && password.length < 10) {
      setError('Adgangskode skal være mindst 10 tegn');
      return;
    }

    if (password && !/[A-Z]/.test(password)) {
      setError('Adgangskode skal indeholde mindst ét stort bogstav');
      return;
    }

    if (password && !/[0-9]/.test(password)) {
      setError('Adgangskode skal indeholde mindst ét tal');
      return;
    }

    setSaving(true);

    try {
      const updateData: any = {
        name,
        phone,
        companyName,
        address,
        postalCode,
        city,
        cvr,
      };

      // Only include password if provided
      if (password) {
        updateData.password = password;
        updateData.confirmPassword = confirmPassword;
      }

      const result = await apiService.updateUserProfile(updateData);

      setSuccess('Profil opdateret');
      setPassword('');
      setConfirmPassword('');

      if (onProfileUpdated && result.profile) {
        onProfileUpdated(result.profile);
      }

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke opdatere profil');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Min profil</h2>
          <button className="close-btn" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Henter profil...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                disabled
                className="input-disabled"
              />
              <span className="field-hint">Email kan ikke ændres</span>
            </div>

            <div className="form-group">
              <label htmlFor="name">Navn</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dit fulde navn"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Tlf</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Telefonnummer"
              />
            </div>

            <div className="form-group">
              <label htmlFor="companyName">Firmanavn</label>
              <input
                type="text"
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Firmanavn"
              />
            </div>

            <div className="form-group">
              <label htmlFor="address">Adresse</label>
              <input
                type="text"
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Vejnavn og nummer"
              />
            </div>

            <div className="form-row">
              <div className="form-group form-group-small">
                <label htmlFor="postalCode">Postnr</label>
                <input
                  type="text"
                  id="postalCode"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="1234"
                  maxLength={4}
                />
              </div>
              <div className="form-group form-group-large">
                <label htmlFor="city">By</label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Bynavn"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="cvr">CVR nr.</label>
              <input
                type="text"
                id="cvr"
                value={cvr}
                onChange={(e) => setCvr(e.target.value)}
                placeholder="12345678"
                maxLength={8}
              />
            </div>

            <div className="section-divider">
              <span>Skift adgangskode (valgfrit)</span>
            </div>

            <div className="form-group">
              <label htmlFor="password">Ny adgangskode</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 10 tegn, stort bogstav, tal"
              />
              {password && (password.length < 10 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) && (
                <span className="field-hint field-error">
                  {password.length < 10 && `Min. 10 tegn (${password.length}/10). `}
                  {!/[A-Z]/.test(password) && 'Mangler stort bogstav. '}
                  {!/[0-9]/.test(password) && 'Mangler tal.'}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Bekræft adgangskode</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Gentag adgangskode"
              />
              {confirmPassword && password !== confirmPassword && (
                <span className="field-hint field-error">Adgangskoder matcher ikke</span>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Annuller
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Gemmer...' : 'Gem ændringer'}
              </button>
            </div>
          </form>
        )}

        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .modal-content {
            background-color: white;
            border-radius: 12px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }

          .modal-header h2 {
            font-size: 24px;
            font-weight: 600;
            color: #333;
            margin: 0;
          }

          .close-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: #666;
            border-radius: 6px;
            transition: background-color 0.2s, color 0.2s;
          }

          .close-btn:hover {
            background-color: #f3f4f6;
            color: #333;
          }

          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px;
          }

          .spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #e5e7eb;
            border-top-color: #3c6c34;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .loading-container p {
            margin-top: 16px;
            color: #666;
          }

          .error-message {
            background-color: #fef2f2;
            border: 1px solid #fecaca;
            color: #dc2626;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 16px;
            font-size: 14px;
          }

          .success-message {
            background-color: #f0fdf4;
            border: 1px solid #bbf7d0;
            color: #3c6c34;
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 16px;
            font-size: 14px;
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            font-size: 14px;
            font-weight: 500;
            color: #374151;
            margin-bottom: 6px;
          }

          .form-group input {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            transition: border-color 0.2s, box-shadow 0.2s;
            box-sizing: border-box;
          }

          .form-group input:focus {
            outline: none;
            border-color: #3c6c34;
            box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
          }

          .input-disabled {
            background-color: #f3f4f6;
            color: #6b7280;
            cursor: not-allowed;
          }

          .field-hint {
            font-size: 12px;
            color: #9ca3af;
            margin-top: 4px;
            display: block;
          }

          .field-hint.field-error {
            color: #dc2626;
            font-weight: 500;
          }

          .form-row {
            display: flex;
            gap: 12px;
          }

          .form-group-small {
            flex: 0 0 100px;
          }

          .form-group-large {
            flex: 1;
          }

          .section-divider {
            display: flex;
            align-items: center;
            margin: 24px 0 16px;
          }

          .section-divider::before,
          .section-divider::after {
            content: '';
            flex: 1;
            border-bottom: 1px solid #e5e7eb;
          }

          .section-divider span {
            padding: 0 12px;
            font-size: 13px;
            color: #6b7280;
          }

          .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e5e7eb;
          }

          .btn-primary {
            background-color: #3c6c34;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          }

          .btn-primary:hover:not(:disabled) {
            background-color: #3c6c34;
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            background-color: white;
            color: #374151;
            border: 1px solid #d1d5db;
            padding: 10px 20px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
          }

          .btn-secondary:hover {
            background-color: #f9fafb;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ProfileModal;
